// LoadsTrendChart — "Loads — month over month" on Customer analytics.
//
// The customer receives this report, so the same SVG is used on screen
// and in print/PDF (what you see is what they get). A plain SVG with a
// fixed viewBox prints at the right size; Recharts did not.
//
//   data: [{ month: 'Aug 2025', loads: 42, priorLoads: 37 }, ...]
//
// Column chart (2026-10-22; a smoothed line looked wavy on uneven months):
//   - one blue column per month with its number on top
//   - when the customer has prior-year history, a light grey column for
//     the same month last year sits beside it (also labelled)
//   - no y-axis: every column carries its own number

const BLUE = '#0a8b3a';   // EcoVac brand green (name kept for the code below)
const GREY_BAR = '#cbd5e1';
const INK = '#0f172a';
const MUTED = '#64748b';
const BASE = '#cbd5e1';

// "Aug 2025" -> "Aug '25"
const shortMonth = (s) => String(s || '').replace(/^(\w{3}) \d{2}(\d{2})$/, "$1 '$2");
const fmt = (v) => Math.round(Number(v) || 0).toLocaleString('en-CA');

// Rounded-top column (square bottom on the baseline)
function column(x, y, w, h, r) {
  if (h <= 0) return '';
  const rr = Math.min(r, w / 2, h);
  return 'M' + x + ',' + (y + h)
    + ' L' + x + ',' + (y + rr)
    + ' Q' + x + ',' + y + ' ' + (x + rr) + ',' + y
    + ' L' + (x + w - rr) + ',' + y
    + ' Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + rr)
    + ' L' + (x + w) + ',' + (y + h) + ' Z';
}

export default function LoadsTrendChart({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  // ── Layout (viewBox units; the SVG scales to its container) ──
  const W = 900, H = 250;
  const L = 8, R = 8, T = 38, B = 30;
  const plotW = W - L - R, plotH = H - T - B;

  const cur = data.map(d => Number(d.loads) || 0);
  const pri = data.map(d => Number(d.priorLoads) || 0);
  const showPrior = pri.some(v => v > 0);
  const max = Math.max(1, ...cur, ...(showPrior ? pri : [0]));
  const yTop = max * 1.12;                       // room for the numbers
  const hOf = (v) => (v / yTop) * plotH;
  const baseY = T + plotH;

  const n = data.length;
  const slot = plotW / n;
  const barW = showPrior ? Math.min(26, slot * 0.34) : Math.min(44, slot * 0.58);
  const gap = showPrior ? 4 : 0;

  return (
    <svg className="loads-trend" viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="xMidYMid meet"
         role="img" aria-label="Loads per month"
         style={{ width: '100%', height: 'auto', display: 'block', fontFamily: 'inherit' }}>

      {/* Legend */}
      <g transform={'translate(' + (L + 4) + ',14)'} fontSize="12" fill={INK}>
        <rect x="0" y="-6" width="12" height="12" rx="2.5" fill={BLUE} />
        <text x="18" y="4">Last 12 months</text>
        {showPrior && (
          <g transform="translate(130,0)">
            <rect x="0" y="-6" width="12" height="12" rx="2.5" fill={GREY_BAR} />
            <text x="18" y="4">Same month, prior year</text>
          </g>
        )}
      </g>

      {/* Baseline */}
      <line x1={L} x2={W - R} y1={baseY} y2={baseY} stroke={BASE} strokeWidth="1" />

      {data.map((d, i) => {
        const cx = L + slot * i + slot / 2;
        const groupW = showPrior ? barW * 2 + gap : barW;
        const x0 = cx - groupW / 2;
        const curX = showPrior ? x0 + barW + gap : x0;
        const hc = hOf(cur[i]), hp = hOf(pri[i]);
        return (
          <g key={i}>
            {showPrior && (
              <g>
                <path d={column(x0, baseY - hp, barW, hp, 4)} fill={GREY_BAR} />
                <text x={x0 + barW / 2} y={baseY - hp - 5} fontSize="10.5" fill={MUTED} textAnchor="middle">
                  {fmt(pri[i])}
                </text>
              </g>
            )}
            <path d={column(curX, baseY - hc, barW, hc, 4)} fill={BLUE} />
            <text x={curX + barW / 2} y={baseY - hc - 6} fontSize="12.5" fontWeight="700" fill={INK} textAnchor="middle">
              {fmt(cur[i])}
            </text>
            <text x={cx} y={H - 10} fontSize="11.5" fill={MUTED} textAnchor="middle">{shortMonth(d.month)}</text>
          </g>
        );
      })}
    </svg>
  );
}
